import { useState } from 'react';
import { Alert, Button, Card, Space, Typography, Upload } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { api } from '../lib/api';

export default function AdminExportImportPage() {
  const [configuration, setConfiguration] = useState<{ apps: unknown[] } | null>(null);
  const [filename, setFilename] = useState('');
  const [busy, setBusy] = useState<'export' | 'import' | 'read' | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const exportApps = async () => {
    setBusy('export'); setError(''); setSuccess('');
    try {
      const data = await api.admin.exportAppsConfiguration();
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2) + '\n'], { type: 'application/json' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `apps-configuration-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Export failed');
    } finally { setBusy(null); }
  };

  const importApps = async () => {
    if (!configuration) return;
    setBusy('import'); setError(''); setSuccess('');
    try {
      const result = await api.admin.importAppsConfiguration(configuration);
      setSuccess(`Import complete: ${result.created} apps created, ${result.updated} apps updated, ${result.prices} prices imported.`);
      setConfiguration(null); setFilename('');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Import failed');
    } finally { setBusy(null); }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={2}>Export/Import</Typography.Title>
        <Typography.Paragraph>Move apps configuration between instances using a JSON file.</Typography.Paragraph>
      </div>
      {error && <Alert type="error" showIcon message={error} />}
      {success && <Alert type="success" showIcon message={success} />}
      <Card title="Export apps configuration">
        <Typography.Paragraph>Includes all apps and sub-apps, display settings, access flags, links, Stripe product IDs and prices. User data and subscriptions are excluded.</Typography.Paragraph>
        <Button icon={<DownloadOutlined />} loading={busy === 'export'} disabled={busy !== null} onClick={exportApps}>Export apps</Button>
      </Card>
      <Card title="Import apps configuration">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            Apps are matched by slug and prices by Stripe price ID. Matching configuration is updated and missing entries are created. Apps and prices absent from the file are kept. The entire import is validated and saved together.
          </Typography.Paragraph>
          <Alert type="info" showIcon message="Links and Stripe IDs are copied as configured. Check that they are suitable for this instance, especially when moving between test and production environments." />
          <Upload accept=".json,application/json" showUploadList={false} disabled={busy !== null}
            beforeUpload={async (file) => {
              setBusy('read'); setConfiguration(null); setFilename(''); setError(''); setSuccess('');
              try {
                if (file.size > 5 * 1024 * 1024) throw new Error('Choose a JSON file smaller than 5 MB.');
                const parsed = JSON.parse((await file.text()).replace(/^\uFEFF/, ''));
                if (!parsed || !Array.isArray(parsed.apps)) throw new Error('The file must contain an apps array.');
                setConfiguration(parsed); setFilename(file.name);
              } catch (error) {
                setError(error instanceof SyntaxError ? 'The file is not valid JSON.' : error instanceof Error ? error.message : 'Unable to read file');
              } finally { setBusy(null); }
              return false;
            }}>
            <Button icon={<UploadOutlined />} disabled={busy !== null}>Choose JSON file</Button>
          </Upload>
          {configuration && <Typography.Text>{filename} — {configuration.apps.length} apps ready to import.</Typography.Text>}
          <Button type="primary" loading={busy === 'import'} disabled={!configuration || busy !== null} onClick={importApps}>Import apps</Button>
        </Space>
      </Card>
    </Space>
  );
}
